import {useMemo} from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {AccountStatus, UserType} from 'crqlar-react-common/dist/models/identity/user.model';
import {UserOrganizationStatus} from 'crqlar-react-common/dist/models/identity/userAccess.model';
import type {UserAccess} from 'crqlar-react-common/dist/models/identity/userAccess.model';
import {useOrganizationAppsQuery} from 'crqlar-react-common/dist/utils/hooks/app/useOrganizationApps';
import {useAllUsers} from 'crqlar-react-common/dist/utils/hooks/users/useUsers';
import {useTranslation} from 'react-i18next';

import {
    USER_TYPE_ORDER,
    hasTeamMemberPicks,
    readAppFilter,
    readExcludedUserFilter,
    readUserFilter,
    readUserTypes,
    userTypeGroupLabel,
    userTypeLabel,
    writeAppFilter,
    writeExcludedUserFilter,
    writeUserFilter
} from '../../../config/teamMembers';
import {MultiSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import NodeEditorLayout from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';

type TeamUser = NonNullable<UserAccess['user']>;

const fullName = (user?: UserAccess['user']): string =>
    [user?.firstName, user?.lastName].filter(Boolean).join(' ');

const displayName = (user: TeamUser): string => fullName(user) || user.email || user.id || '';

/**
 * The same set the API resolves at run time. Invited users often have no `accountStatus`, so only ARCHIVED
 * excludes (as login does); a pending invitation is caught by the membership status.
 */
const isActiveMember = (member: UserAccess): boolean => (
    member.status === UserOrganizationStatus.ACTIVE
    && member.user?.accountStatus !== AccountStatus.ARCHIVED
    && !member.user?.archived
);

/** A colleague with no app restriction of their own can open every app — the API reads it the same way. */
const canOpenAny = (user: TeamUser, appIds: string[]): boolean =>
    !user.permittedAppIds || appIds.some(id => user.permittedAppIds!.includes(id));

/**
 * The Team members step: the three fields combine with OR; nothing picked = nobody (and it cannot go live).
 * People shows everyone a type or app brings in ticked "via …": unticking writes `excludedUserIds`, ticking
 * anyone else `userIds`. The summary only counts.
 */
const TeamMembersEditorV2 = ({nodeConfig, setNodeConfig, readOnly}: NodeEditorV2Props) => {
    const {t, i18n} = useTranslation();

    const appsQuery = useOrganizationAppsQuery();
    const organizationApps = useMemo(() => appsQuery.data?.content ?? [], [appsQuery.data]);
    const {data: users = [], isLoading: usersLoading} = useAllUsers();

    const userTypes = readUserTypes(nodeConfig);
    const appFilter = readAppFilter(nodeConfig);
    const userFilter = readUserFilter(nodeConfig);
    const excludedFilter = readExcludedUserFilter(nodeConfig);
    const picked = hasTeamMemberPicks(nodeConfig);

    // The users endpoint does not sort: order by the shown name in the UI language's collation.
    const language = i18n.resolvedLanguage || i18n.language;
    const members = useMemo(() => {
        const collator = new Intl.Collator(language, {sensitivity: 'base'});
        return users
            .filter(member => isActiveMember(member) && !!member.user?.id)
            .sort((left, right) => collator.compare(displayName(left.user!), displayName(right.user!)));
    }, [users, language]);

    // A row's own reach, independent of the other fields (the summary has the combined count); none while loading.
    const countOf = (reaches: (user: TeamUser) => boolean): string | undefined => {
        if (usersLoading) return undefined;
        const count = members.filter(({user}) => !!user?.id && reaches(user)).length;
        return count === 1 ? t('1 person') : t('{{count}} people', {count});
    };

    // The select keeps the order it is handed (USER_TYPE_ORDER); both group checkboxes = the whole team.
    const userTypeOptions: SelectOption[] = USER_TYPE_ORDER.map(userType => ({
        value: userType,
        label: userTypeLabel(userType, t),
        group: userTypeGroupLabel(userType, t),
        detail: countOf(user => user.userType === userType)
    }));

    const appOptions: SelectOption[] = organizationApps
        .filter(app => !!app.id)
        .map(app => ({
            value: app.id!,
            label: app.name || app.id!,
            detail: countOf(user => canOpenAny(user, [app.id!]))
        }));

    // What already brings a colleague in: their user type, else the first picked app they can open.
    const coveredBy = (user: TeamUser): string | undefined => {
        if (userTypes.includes(user.userType as UserType)) return userTypeLabel(user.userType!, t);
        const appId = appFilter.ids.find(id => canOpenAny(user, [id]));
        return appId ? organizationApps.find(app => app.id === appId)?.name || appFilter.names[appId] || appId : undefined;
    };

    // Uncovered rows show the email to tell same-name colleagues apart, unless the label already is the email.
    const coveredIds: string[] = [];
    const userOptions: SelectOption[] = members.map(({user}) => {
        const via = coveredBy(user!);
        if (via) coveredIds.push(user!.id!);

        return {
            value: user!.id!,
            label: displayName(user!),
            detail: via ? t('via {{name}}', {name: via}) : fullName(user) ? user!.email : undefined
        };
    });

    // As the API resolves it: named people always, plus anyone a type or app brings in unless excluded.
    const matchingMembers = picked
        ? members.filter(({user}) => userFilter.ids.includes(user!.id!)
            || (coveredIds.includes(user!.id!) && !excludedFilter.ids.includes(user!.id!)))
        : [];

    const reachesNobody = !picked || (!usersLoading && !matchingMembers.length);

    const summary = !picked
        ? t('Nobody is picked yet, so no one goes through the steps below.')
        : usersLoading
        ? t('Loading...')
        : !matchingMembers.length
        ? t('Nobody in the team matches these picks, so no one goes through the steps below.')
        : t('Team members summary', {count: matchingMembers.length});

    // Updaters, not snapshots: the People field reports a pick and an opt-out in the same click.
    const handleAppsChange = (ids: string[]) => setNodeConfig(prev => ({
        ...prev,
        ...writeAppFilter(ids.map(id => ({
            id,
            name: organizationApps.find(app => app.id === id)?.name || appFilter.names[id]
        })))
    }));

    const handleUsersChange = (ids: string[]) => setNodeConfig(prev => ({
        ...prev,
        ...writeUserFilter(ids.map(id => ({
            id,
            name: fullName(members.find(member => member.user?.id === id)?.user) || userFilter.names[id]
        })))
    }));

    const handleExcludedChange = (ids: string[]) => setNodeConfig(prev => ({
        ...prev,
        ...writeExcludedUserFilter(ids.map(id => ({
            id,
            name: fullName(members.find(member => member.user?.id === id)?.user) || excludedFilter.names[id]
        })))
    }));

    // The If step's divider: the same words for the same logic.
    const orDivider = (
        <Typography variant={'body2'} align={'center'} color={'success.main'} sx={{fontWeight: 600, letterSpacing: '0.08em'}}>
            {t('OR — alternatively')}
        </Typography>
    );

    return (
        <NodeEditorLayout state={reachesNobody ? 'wontRun' : 'configured'} summary={summary}>
            <Stack gap={1.5}>
                <MultiSelect
                    label={t('User types')}
                    value={userTypes}
                    options={userTypeOptions}
                    onChange={values => setNodeConfig(prev => ({...prev, userTypes: values}))}
                    disabled={readOnly}
                    bulkSelect
                />

                {orDivider}

                <MultiSelect
                    label={t('People with access to')}
                    value={appFilter.ids}
                    options={appOptions}
                    unknownLabels={appFilter.names}
                    loading={appsQuery.isLoading || undefined}
                    onChange={handleAppsChange}
                    disabled={readOnly}
                />

                {orDivider}

                <MultiSelect
                    label={t('People')}
                    value={userFilter.ids}
                    options={userOptions}
                    unknownLabels={userFilter.names}
                    loading={usersLoading || undefined}
                    onChange={handleUsersChange}
                    disabled={readOnly}
                    impliedValues={coveredIds}
                    excludedValues={excludedFilter.ids}
                    onExcludedChange={handleExcludedChange}
                />
            </Stack>
        </NodeEditorLayout>
    );
};

export default TeamMembersEditorV2;
